import { internalReference } from './header';
import {
  bynderImageProjection,
  computedInternalUrl,
  directInternalReferenceUrl,
  internalReferenceUrl,
  spreadBynderImageProjection,
} from './projection';

export const PRODUCT_REFERENCE_INFO = `
productRef->{
  _id,
  _type,
  name,
  "productURL": select(
    defined(parentPage->parentPage->slug.current) => "/products/" + parentPage->parentPage->slug.current + "/" + parentPage->slug.current + "/" + slug.current,
    defined(parentPage->slug.current) => "/products/" + parentPage->slug.current + "/" + slug.current,
    _type == "product" && defined(subcategory->parentCategory->slug.current) => "/products/" + subcategory->parentCategory->slug.current + "/" + subcategory->slug.current + "/" + slug.current,
    "/products/" + slug.current
  )
}
`;

export const getInternalUrlQuery = (contentName: string) => `
  ${contentName}[]{
    ...,
    markDefs[]{
      ...,
      "href": select(
        linkType == "page" => ${internalReference('page')}.url,
        href
      ),
      "isOpenInNewTab": select(
        linkType == "page" => blank,
        true
      ),
    }
  }
`;

/* ========================================================================= */

export const documentBlockProjection = `
_type == "documentBlock" => {
  ...,
  ${bynderImageProjection('image', 'bynderImage')},
  bynderImage,
  "defaultExpanded": [],
  documentSections[]{
    ...,
    documentItems[]{
      ...,
      "bynderDocument": file->bynderDocument
    }
  }
}
`;

export const applicationsBlockProjection = `
  _type == "applicationsBlock" => {
    ...,
    buttons[]{
      ...,
      link{
        ...,
        "url": ${internalReferenceUrl},
        _type == "link" => {
          "page": page->slug.current,
          "post": post->slug.current
        },
      },
    },
    "items": select(
      selectionMode == "manual" => items[]->{
        ...,
        "thumbnail": customSeo.metaImage{ ..., "url": selectedUrl, "alt": textMetaproperties[name == 'Alt_text'][0].value },
        "date": coalesce(date, _updatedAt),
      },
      selectionMode == "auto" => *[_type == "article" && type == ^.contentType && references(^.tag._ref) && defined(slug.current)] | order(_updatedAt desc) [0...3]{
        ...,
        "thumbnail": customSeo.metaImage{ ..., "url": selectedUrl, "alt": textMetaproperties[name == 'Alt_text'][0].value },
        "date": coalesce(date, _updatedAt),
      }
    )
  }
`;

export const textSectionProjection = `
  _type == "textSection" => {
    ...,
    ${getInternalUrlQuery('content')},
    columns[]{
      ...,
      ${getInternalUrlQuery('content')},
    }
  }
`;

export const imageTextBlockProjection = `
_type == "imageTextBlock" => {
  ...,
  ${bynderImageProjection('image', 'bynderImage')},
  bynderImage,
  buttons[]{
    ...,
    link{ href, isOpenInNewTab }
  },
  ${getInternalUrlQuery('body')},
}
`;

export const featureSummaryProjection = `
_type == "featureSummary" => {
  ...,
  contentBlocks[]{
    ...,
    _key,
    ${bynderImageProjection('icon', 'bynderIcon')},
    bynderIcon,
    textLink{
      text,
      isBold,
      "link": {
        "type": link.type,
         "href": ${directInternalReferenceUrl},
        "isOpenInNewTab": coalesce(link.isOpenInNewTab, link.newTab)
      }
    }
  }
}
`;

export const productTypeProjection = `
_type == "productType" => {
  ...,
  buttons[]{
    ...,
    link{
      ...,
      "url": ${internalReferenceUrl},
    }
  },
  "productCards": productCards[]{
    ...,
    _id,
    "productDetail": productDetail->{
      ...,
      _id,
      "name": coalesce(name, title),
      "productSummary": coalesce(productSummary, description),
      "thumbnailImage": customSeo.metaImage{
        ...,
        "alt": coalesce(textMetaproperties[name == 'Alt_text'][0].value, alt)
      },
      "url": ${computedInternalUrl},
    }
  }
}
`;

export const contactSupportBlockProjection = `
  _type == "contactSupportBlock" => {
    ...,
    imageLink{
      ${bynderImageProjection('image', 'bynderImage', "coalesce(bynderImage.textMetaproperties[name == 'Alt_text'][0].value, bynderImage.alt, alt)")},
      bynderImage,
      "alt": coalesce(bynderImage.textMetaproperties[name == 'Alt_text'][0].value, bynderImage.alt, alt),
      link{
        ...,
        "url": ${internalReferenceUrl},
      }
    },
    contactInfo[]{
      ...,
      icon{
        icon,
        ${bynderImageProjection('iconImage', 'bynderIconImage')},
        bynderIconImage
      },
      textLink{
        text,
        isBold,
        "link": {
          "href": ${internalReferenceUrl},
          "isOpenInNewTab": coalesce(link.isOpenInNewTab, link.newTab)
        }
      }
    },
    ctaButton{
      ...,
      "link": {
        ...,
        "href": ${internalReferenceUrl},
        "isOpenInNewTab": coalesce(link.isOpenInNewTab, link.newTab)
      }
    }
  }
`;

export const heroBannerSlideshowProjection = `
  _type in ["heroBannerSlideshow", "heroBanner"] => {
    ...,
    _type,
    isActive,
    "slides": slides[]{
      ...,
      _type,
      _key,
      isVideo,
      ${bynderImageProjection('image', 'bynderImage')},
      bynderImage,
      ${bynderImageProjection('poster', 'bynderPoster')},
      bynderPoster,
      bynderVideo,
      "videoFile": select(
        defined(bynderVideo) => {
          "_type": "bynder.asset",
          "id": bynderVideo.id,
          "name": bynderVideo.name,
          "description": bynderVideo.description,
          "selectedUrl": bynderVideo.selectedUrl,
          "videoUrl": bynderVideo.videoUrl,
          "url": coalesce(bynderVideo.videoUrl, bynderVideo.selectedUrl, bynderVideo.url)
        },
        null
      ),
      "videoUrl": video,
      "buttons": buttons[]{
        label,
        style,
        "url": ${directInternalReferenceUrl},
        "anchor": coalesce(link.anchor, linkWithAskGreg.anchor),
        "linkType": coalesce(link.type, linkWithAskGreg.type),
        "isOpenInNewTab": coalesce(link.isOpenInNewTab, linkWithAskGreg.isOpenInNewTab)
      },
    }
  }
`;

export const contentCarouselProjection = `
  _type == "contentCarousel" => {
    ...,
    "description": description,
    "backgroundColor": backgroundColor,
    "switchImageAndText": switchImageAndText,
    ${getInternalUrlQuery('description')},
    slides[]{
      ...,
      "vimeoURL": video,
      ${bynderImageProjection('image', 'bynderImage')},
      bynderImage,
      ${getInternalUrlQuery('description')},
    }
  }
`;

export const videoImageBlockProjection = `
_type == "videoImageBlock" => {
  ...,
  _type,
  "videoUrl": video,
  textOverlay {
    heading,
    description
  },
  ${bynderImageProjection('thumbnail', 'bynderThumbnail')},
  bynderThumbnail,
  ${bynderImageProjection('image', 'bynderImage')},
  bynderImage,
   buttons[]{
    label,
    "style": style,
    "url": ${directInternalReferenceUrl},
    "anchor": link.anchor,
    "linkType": link.type,
    "isOpenInNewTab": link.isOpenInNewTab
  }
}
`;

export const logoWallProjection = `
  _type == "logoWall" => {
    ...,
    "backgroundColor": backgroundColor,
    heading,
    description[]{
      ...,
      markDefs[]{
        ...,
        _type == "link" => {
          ...,
          "url": href,
          "isOpenInNewTab": coalesce(blank, false)
        }
      }
    },
    textAlignment,
    "logos": logos[] {
        _key,
        "bynderLogoImage": select(
          defined(selectedUrl) || defined(url) || defined(datUrl) || defined(previewUrl) || defined(previewImg) => {
            "_type": "bynder.asset",
            "id": id,
            "name": name,
            "description": description,
            "alt": coalesce(textMetaproperties[name == 'Alt_text'][0].value, alt),
            "selectedUrl": selectedUrl,
            "url": coalesce(url, selectedUrl, datUrl, previewUrl, previewImg),
            "width": width,
            "height": height,
            "aspectRatio": aspectRatio
          },
          null
        ),
        "url": coalesce(
          selectedUrl,
          url,
          datUrl,
          previewUrl,
          previewImg,
          asset->url
      ),
      "alt": coalesce(textMetaproperties[name == 'Alt_text'][0].value, alt, asset->altText),
    },
    ${getInternalUrlQuery('description')}
  }
`;

export const headlineProjection = `
_type == "headline" => {
  ...,
  title,
  shortSummary,
  "backgroundColor": backgroundColor,
  "headingAlignment": coalesce(headingAlignment, "left"),
  "isActive": coalesce(isActive, false)
}
`;

export const quoteBlockProjection = `
_type == "quote" => {
  ...,
  ${bynderImageProjection('icon', 'bynderIcon')},
  bynderIcon,
  heading,
  subheading,
  "backgroundColor": backgroundColor
}
`;

export const milestoneTimelineProjection = `
  _type == "milestoneTimeline" => {
    ...,
    heading,
    "backgroundColor": backgroundColor,
    "timelineItems": timelineItems[] {
      ...,
      yearDecade,
      "description": description,
    },
    ${getInternalUrlQuery('description')},
  }
`;

export const masterTextBlockProjection = `
  _type == "masterTextBlock" => {
    ...,
    heading,
    hyperlink {
      text,
      isBold,
      "link": {
        "href": ${directInternalReferenceUrl},
        "isOpenInNewTab": coalesce(link.isOpenInNewTab, link.newTab)
      }
    },
    "description": description,
    "backgroundColor": backgroundColor,
    "layoutOptions": layoutOptions,
    "textBlocks": textBlocks[] {
      ...,
      ${getInternalUrlQuery('description')},
    },
    ${getInternalUrlQuery('description')},
  }
`;

export const productSlideshowProjection = `
  _type == "productSlideshow" => {
    ...,
    "backgroundColor": backgroundColor,
    "productSlides": productSlides[] {
      ...,
      heading,
      subheading,
      description,
      ${PRODUCT_REFERENCE_INFO},
      productDescription,
      "productThumbnail": productRef->customSeo.metaImage{
        ...,
        "url": selectedUrl,
        "alt": coalesce(textMetaproperties[name == 'Alt_text'][0].value, alt)
      },
      hyperlinkLabel
    }
  }
`;

export const productCarouselProjection = `
  _type == "productCarousel" => {
    ...,
    "backgroundColor": backgroundColor,
    "productCarouselItems": productCarouselItems[] {
      ...,
      ${PRODUCT_REFERENCE_INFO},
      productDescription,
      "productThumbnail": productRef->customSeo.metaImage{
        ...,
        "url": selectedUrl,
        "alt": coalesce(textMetaproperties[name == 'Alt_text'][0].value, alt)
      },
      hyperlinkLabel
    }
  }
`;

export const multiColumnBannerProjection = `
  _type == "multiColumnBanner" => {
    ...,
    heading,
    "layoutOptions": layoutOptions,
    "textBlocks": textBlocks[] {
      ...,
      ${getInternalUrlQuery('description')},
    },
    "headingTextAlignment": headingTextAlignment,
    "backgroundColor": backgroundColor
  }
`;

export const galleryProjection = `
  _type == "gallery" => {
    ...,
    "images": images[]{
      "_key": _key,
      ${spreadBynderImageProjection('bynderSrc')},
    },
  }
`;

export const productVariantProjection = `
  _type == "productVariantSection" => {
    ...,
    buttons[]{
      ...,
      link{
        ...,
        "url": ${internalReferenceUrl},
      }
    },
    productVariantItems[]{
      name,
      description,
      variantValue,
      campaignLink{
        ...,
        "url": ${internalReferenceUrl},
      },
      ${bynderImageProjection('defaultImage', 'defaultImage->bynderImage')},
      "bynderImage": defaultImage->bynderImage,
    },
  }
`;

export const productSummaryProjection = `
  _type == "productSummary" => {
    ...,
    ${bynderImageProjection('image', 'bynderImage')},
    bynderImage,
    ${getInternalUrlQuery('description')},
    ctaUnderHeadline[]{
      ...,
      "url": select(
        linkType == "internal" => internalReference->{
          "computedUrl": ${computedInternalUrl}
        }.computedUrl,
        url
      ),
    },
    ctaUnderDescription[]{
      ...,
      "url": select(
        linkType == "internal" => internalReference->{
          "computedUrl": ${computedInternalUrl}
        }.computedUrl,
        url
      ),
    }
  }
`;

export const middleBannerProjection = `
  _type == "middleBanner" => {
    ...,
    ${getInternalUrlQuery('bodyContent')},
    buttonOptional{
      ...,
      "linkOptional": link{
        ...,
        "url": ${internalReferenceUrl},
        "isOpenInNewTab": coalesce(isOpenInNewTab, newTab)
      }
    }
  }
`;

export const textIconListProjection = `
  _type == "textIconList" => {
    ...,
    ${getInternalUrlQuery('description')},
    textIconItemList{
      ...,
      subheading,
      textIconItems[]{
        ...,
        ${bynderImageProjection('icon', 'bynderIcon')},
        bynderIcon,
        title{
          ...,
          isBold,
          text,
          link{
            ...,
            "type": link.type,
            "href": ${internalReferenceUrl},
            "isOpenInNewTab": coalesce(link.isOpenInNewTab, link.newTab)
          },
        },
        ${getInternalUrlQuery('description')},
      }
    }
  }
`;

export const imageGridProjection = `
  _type == "imageGrid" => {
    ...,
    images[] {
      ...,
      ${bynderImageProjection('image', 'bynderImage', "coalesce(bynderImage.textMetaproperties[name == 'Alt_text'][0].value, bynderImage.alt, alt)")},
      bynderImage,
      "alt": coalesce(bynderImage.textMetaproperties[name == 'Alt_text'][0].value, bynderImage.alt, alt),
    },
  }
`;

export const iconTextColumnsBlockProjection = `
  _type == "iconTextColumnsBlock" => {
    ...,
    "backgroundColor": backgroundColor,
    columns[] {
      ...,
      heading,
      textColor,

      listItems[] {
        ...,

        ${bynderImageProjection('icon', 'listIcon')},
        listIcon,

        ${getInternalUrlQuery('richContent')},
      }
    }
  }
`;

export const PAGE_BUILDER_PROJECTION = `
  "pageBuilder" : pageBuilder[]{
    ...,
    ${applicationsBlockProjection},
    ${textSectionProjection},
    ${imageTextBlockProjection},
    ${featureSummaryProjection},
    ${productTypeProjection},
    ${heroBannerSlideshowProjection},
    ${contentCarouselProjection},
    ${videoImageBlockProjection},
    ${documentBlockProjection},
    ${logoWallProjection},
    ${headlineProjection},
    ${quoteBlockProjection},
    ${milestoneTimelineProjection},
    ${masterTextBlockProjection},
    ${multiColumnBannerProjection},
    ${productSlideshowProjection},
    ${productCarouselProjection},
    ${contactSupportBlockProjection},
    ${galleryProjection},
    ${productVariantProjection},
    ${productSummaryProjection},
    ${middleBannerProjection},
    ${textIconListProjection},
    ${imageGridProjection},
    ${iconTextColumnsBlockProjection},
  }
`;
